import { useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import { useCallback, useMemo } from 'react';
import { BirthdayPicker, FormLabel, Select, SelectOption } from 'ui-components';
import { formatDateStringForDatePicker } from '~/core/utils/date';
import { TalentValues } from '~/screens/AddTalent/ProfileInformation';
import { talentFiltersTrackId } from '~/screens/MyAgency/components/CreatorsFiltersDrawer/constants';
import {
  CreatorLocationSuggestion,
  ILocation,
  SearchLocation,
} from '~/screens/MyAgency/components/SearchLocation';

type Props = {};

export const LocationGenderDob: React.FC<Props> = () => {
  const { values, setFieldValue, setErrors, errors } =
    useFormikContext<TalentValues>();

  const sPLocation = values.smartData?.location?.value;
  const sPLocationLoading =
    !values.location && values.smartData?.location?.status === 'pending';
  const isLocationSmart = useMemo(
    () => (values.location ? isEqual(sPLocation, values.location) : false),
    [sPLocation, values.location],
  );

  const sPDob = values.smartData?.dateOfBirth?.value;
  const sPDobLoading =
    !values.dateOfBirth && values.smartData?.dateOfBirth?.status === 'pending';
  const isSmartDOB = useMemo(() => {
    if (!values.dateOfBirth || !sPDob) return false;

    try {
      const formattedDate = formatDateStringForDatePicker(sPDob);
      return formattedDate === values.dateOfBirth;
    } catch (error) {
      return false;
    }
  }, [sPDob, values.dateOfBirth]);

  const sPGender = values.smartData?.gender?.value;
  const sPGenderLoading =
    !values.gender && values.smartData?.gender?.status === 'pending';
  const isGenderFromSmartData = useMemo(
    () => (values.gender ? sPGender === values.gender : false),
    [sPGender, values.gender],
  );

  const onChangeLocation = useCallback(
    (field?: ILocation | CreatorLocationSuggestion) => {
      setFieldValue('location', field);
    },
    [setFieldValue],
  );

  const onLocationError = useCallback(
    (error?: boolean) => {
      if (error) return setErrors({ ...errors, location: 'invalid' });
      const { location: _, ...rest } = errors;
      setErrors(rest);
    },
    [errors, setErrors],
  );

  return (
    <div className="flex items-start gap-6">
      <div className="flex-1" track-id={talentFiltersTrackId.location}>
        <div>
          <FormLabel htmlFor="location">Location</FormLabel>
          <SearchLocation
            values={values.location}
            setValues={onChangeLocation}
            onError={onLocationError}
            isSmartData={sPLocationLoading || isLocationSmart}
            isLoading={sPLocationLoading}
            trackId="foam-add-talent-profile-info-location"
            inputProps={{
              clearButtonProps: {
                'aria-label': 'Clear location',
                'track-id': 'foam-add-talent-profile-info-location-clear',
              },
            }}
          />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-1">
        <FormLabel>Gender</FormLabel>
        <div
          className="py-1"
          track-id="foam-talent-profileInformationModal-genderCreator"
        >
          <Select
            placeholder="Select"
            value={values.gender}
            onChange={(opt) => setFieldValue('gender', opt.value)}
            showPlaceholder
            margin={0}
            isSmartData={sPGenderLoading || isGenderFromSmartData}
            isLoading={sPGenderLoading}
            data-testid="creator-profile-gender"
            trackId="foam-add-talent-profile-info-gender"
          >
            <SelectOption value="Male">Male</SelectOption>
            <SelectOption value="Female">Female</SelectOption>
            <SelectOption value="Non Binary">Non Binary</SelectOption>
          </Select>
        </div>
      </div>

      <div className="flex-1">
        <BirthdayPicker
          dataTestId="creator-profile-birthday"
          name="dateOfBirth"
          isSmartData={sPDobLoading || isSmartDOB}
          isLoading={sPDobLoading}
          inputProps={{
            trackId: 'foam-add-talent-profile-info-birthday',
            testId: 'birthday-picker-input',
            clearButtonProps: {
              'aria-label': 'Clear date of birth',
              'data-testid': 'birthday-picker-clear-button',
              trackId: 'foam-add-talent-profile-info-birthday-clear',
            },
          }}
        />
      </div>
    </div>
  );
};
